import cn from "classnames";
import React from "react";
import { styled } from "@linaria/react";
import useToasts from "@ui/Toast/useToasts";
import Button from "@ui/Button";
import { color } from "@ui";
import t from "../../../i18n";
import { usePost } from "../../../api/useCall";
import type { Project } from "../../../api/useProjects";
import useComputationsStatus from "../../../api/useComputationsStatus";
import { useCanUserDoOnProject } from "../../canUserDo";
import { useMutateBody } from "../../../api/useQuery";
import Spinner from "@ui/Spinner";

const CANCELLABLE_CN = "cancellable";

const SpinnerIcon = styled(Spinner)`
  width: 14px;
  margin-right: 5px;
`;

const MagicButton = styled(Button)<{ digitcount: number }>`
  min-width: calc(210px + ${props => props.digitcount});

  &.${CANCELLABLE_CN}:hover {
    background-color: ${color.oxygen400} !important;
  }
`;

interface P extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  project: Project;
}

const MagicActions = ({ project, className }: P) => {
  const projectId = project.id;
  const { addToast } = useToasts();
  const [hovered, setHovered] = React.useState(false); // button is hovered
  const canUserDoStartAction = useCanUserDoOnProject("PROJECT_WRITE", projectId);
  const startProcessesEndpoint = usePost("/projects/:projectId(uuid)/computations/start");
  const stopProcessesEndpoint = usePost("/projects/:projectId(uuid)/computations/stop");
  const { running: computationsRunning, startable: computationsStartable } =
    useComputationsStatus(project);
  const mutateComputations = useMutateBody("/projects/:projectId(uuid)/computations/status", {
    routeParams: { projectId },
  });

  const [cancelAllowed, setCancelAllowed] = React.useState(true); // mouse has left the button once since click

  const handleMouseIn = React.useCallback(() => {
    setHovered(true);
  }, []);

  const handleMouseOut = React.useCallback(() => {
    setHovered(false);
    setCancelAllowed(true);
  }, []);

  const handleMagicAction = React.useCallback(async () => {
    if (hovered && cancelAllowed && computationsRunning) {
      const rollback = mutateComputations(({ running }) => ({
        running: 0,
        startable: running,
      }));
      try {
        await stopProcessesEndpoint({ routeParams: { projectId } });
      } catch (error) {
        rollback();
        throw error;
      }
      addToast("success", t("project.header.actions.computations.stop.success"));
    } else if (computationsStartable) {
      const rollback = mutateComputations(({ startable }) => ({
        running: startable,
        startable: 0,
      }));
      setCancelAllowed(false); // do not allow to cancel immediately (to avoid double click)

      try {
        await startProcessesEndpoint({ routeParams: { projectId } });
      } catch (error) {
        rollback();
        throw error;
      }
      addToast("success", t("project.header.actions.computations.start.success"));
    }
  }, [
    addToast,
    cancelAllowed,
    computationsRunning,
    computationsStartable,
    hovered,
    mutateComputations,
    projectId,
    startProcessesEndpoint,
    stopProcessesEndpoint,
  ]);

  const wording = React.useMemo(() => {
    if (hovered && cancelAllowed && computationsRunning)
      return t("project.header.actions.computations.stop_computations", {
        count: computationsRunning,
      });

    if (computationsRunning)
      return t("project.header.actions.computations.computations_running", {
        count: computationsRunning,
      });

    if (computationsStartable)
      return t("project.header.actions.computations.start_computations", {
        count: computationsStartable,
      });

    return null;
  }, [hovered, cancelAllowed, computationsRunning, computationsStartable]);

  if (!canUserDoStartAction) return null;

  return wording ? (
    <MagicButton
      digitcount={computationsRunning ? Math.floor(Math.log10(computationsRunning)) : 0}
      variant="primary"
      slim
      onClick={handleMagicAction}
      onMouseEnter={handleMouseIn}
      onMouseLeave={handleMouseOut}
      className={cn(className, { [CANCELLABLE_CN]: computationsRunning && cancelAllowed })}
    >
      {computationsRunning ? <SpinnerIcon color={color.hydrogen} /> : null}
      {wording}
    </MagicButton>
  ) : null;
};

export default MagicActions;
